import { isValidStateCode, type StateCode } from "./regions";

export type ZoneId =
  // Sydney (NSW)
  | "eastwood-epping"
  | "strathfield-burwood"
  | "chatswood-north-shore"
  | "parramatta-west"
  | "sydney-cbd"
  | "lidcombe-auburn"
  | "ryde-macquarie"
  | "hornsby-hills"
  // Melbourne (VIC)
  | "melbourne-cbd"
  | "box-hill-doncaster"
  | "glen-waverley-clayton"
  // Brisbane (QLD)
  | "brisbane-cbd"
  | "sunnybank-runcorn"
  // Perth (WA)
  | "perth"
  // Adelaide (SA)
  | "adelaide"
  // Canberra (ACT)
  | "canberra";

export interface Zone {
  id: ZoneId;
  label: string;
  state: Exclude<StateCode, "ALL">;
  city: string;
  suburbs: string[];
  fallbackZoneIds: ZoneId[];
}

export const DEFAULT_ZONE_ID: ZoneId = "sydney-cbd";

export const ZONES: readonly Zone[] = [
  {
    id: "eastwood-epping",
    label: "Eastwood-Epping",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Eastwood", "Epping", "Marsfield", "Denistone", "Carlingford"],
    fallbackZoneIds: ["ryde-macquarie", "strathfield-burwood"],
  },
  {
    id: "strathfield-burwood",
    label: "Strathfield-Burwood",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Strathfield", "Burwood", "Homebush", "Ashfield", "Concord"],
    fallbackZoneIds: ["lidcombe-auburn", "sydney-cbd"],
  },
  {
    id: "chatswood-north-shore",
    label: "Chatswood-North Shore",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Chatswood", "Willoughby", "Artarmon", "St Leonards", "Lane Cove"],
    fallbackZoneIds: ["ryde-macquarie", "hornsby-hills"],
  },
  {
    id: "parramatta-west",
    label: "Parramatta-West",
    state: "NSW",
    city: "Sydney",
    // Use the Dundas/Telopea/Rydalmere corridor identified in the 2021
    // Census-derived review. Keep Parramatta itself for search recognition.
    suburbs: ["Parramatta", "Dundas Valley", "Telopea", "Dundas", "Rydalmere"],
    fallbackZoneIds: ["lidcombe-auburn", "strathfield-burwood"],
  },
  {
    id: "sydney-cbd",
    label: "Sydney CBD",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Sydney", "Haymarket", "Ultimo", "Surry Hills", "Pyrmont"],
    fallbackZoneIds: ["strathfield-burwood", "chatswood-north-shore"],
  },
  {
    id: "lidcombe-auburn",
    label: "Lidcombe-Auburn",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Lidcombe", "Auburn", "Berala", "Regents Park", "Silverwater"],
    fallbackZoneIds: ["strathfield-burwood", "parramatta-west"],
  },
  {
    id: "ryde-macquarie",
    label: "Ryde-Macquarie",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Ryde", "Macquarie Park", "North Ryde", "Meadowbank", "Gladesville"],
    fallbackZoneIds: ["eastwood-epping", "chatswood-north-shore"],
  },
  {
    id: "hornsby-hills",
    label: "Hornsby-Hills",
    state: "NSW",
    city: "Sydney",
    suburbs: ["Hornsby", "Waitara", "Thornleigh", "Castle Hill", "Baulkham Hills"],
    fallbackZoneIds: ["chatswood-north-shore", "eastwood-epping"],
  },
  // ------------------------------------------------------------------
  // Melbourne (VIC). The Victorian Government's 2021 Census community
  // profile identifies Melbourne and Monash as the two largest Victorian
  // LGAs for the South Korean community, followed by Whitehorse and
  // Manningham. These zones cover the matching central, south-eastern and
  // eastern corridors.
  // ------------------------------------------------------------------
  {
    id: "melbourne-cbd",
    label: "Melbourne CBD",
    state: "VIC",
    city: "Melbourne",
    suburbs: ["Melbourne", "West Melbourne", "Docklands", "Southbank", "Carlton"],
    fallbackZoneIds: ["glen-waverley-clayton", "box-hill-doncaster"],
  },
  {
    id: "glen-waverley-clayton",
    label: "Glen Waverley-Clayton",
    state: "VIC",
    city: "Melbourne",
    suburbs: ["Mount Waverley", "Clayton", "Glen Waverley", "Notting Hill", "Chadstone"],
    fallbackZoneIds: ["box-hill-doncaster", "melbourne-cbd"],
  },
  {
    id: "box-hill-doncaster",
    label: "Box Hill-Doncaster",
    state: "VIC",
    city: "Melbourne",
    suburbs: ["Box Hill", "Doncaster", "Doncaster East", "Box Hill North", "Oakleigh East"],
    fallbackZoneIds: ["glen-waverley-clayton", "melbourne-cbd"],
  },
  // ------------------------------------------------------------------
  // Brisbane (QLD). 2021 Census QuickStats confirm a strong South
  // Korea-born and Korean-ancestry presence around Eight Mile Plains and
  // the Sunnybank/Runcorn corridor. The second zone covers the inner-city
  // cluster used for search and content discovery.
  // ------------------------------------------------------------------
  {
    id: "brisbane-cbd",
    label: "Brisbane CBD",
    state: "QLD",
    city: "Brisbane",
    suburbs: ["Brisbane City", "South Brisbane", "West End", "Fortitude Valley", "Woolloongabba"],
    fallbackZoneIds: ["sunnybank-runcorn"],
  },
  {
    id: "sunnybank-runcorn",
    label: "Sunnybank-Runcorn",
    state: "QLD",
    city: "Brisbane",
    suburbs: ["Sunnybank", "Runcorn", "Eight Mile Plains", "Robertson", "MacGregor"],
    fallbackZoneIds: ["brisbane-cbd"],
  },
  // ------------------------------------------------------------------
  // Perth, Adelaide, Canberra: much smaller Korean communities with no
  // clearly documented single enclave, so each ships as one broad zone
  // for now. Split into sub-zones later only if real content density
  // justifies it. Suburb lists here are lower-confidence than Sydney/
  // Melbourne/Brisbane and should be revisited with local input.
  // ------------------------------------------------------------------
  {
    id: "perth",
    label: "Perth",
    state: "WA",
    city: "Perth",
    suburbs: ["Perth", "Victoria Park", "Burswood", "Rivervale", "Cannington"],
    fallbackZoneIds: [],
  },
  {
    id: "adelaide",
    label: "Adelaide",
    state: "SA",
    city: "Adelaide",
    suburbs: ["Adelaide", "Magill", "Payneham", "Campbelltown", "Firle"],
    fallbackZoneIds: [],
  },
  {
    id: "canberra",
    label: "Canberra",
    state: "ACT",
    city: "Canberra",
    suburbs: ["Canberra City", "Reid", "Belconnen", "Woden", "Braddon"],
    fallbackZoneIds: [],
  },
] as const;

const ZONE_BY_ID = new Map<ZoneId, Zone>(ZONES.map((zone) => [zone.id, zone]));
const SUBURB_TO_ZONE = new Map<string, ZoneId>(
  ZONES.flatMap((zone) =>
    zone.suburbs.map((suburb) => [normalizeLocationToken(suburb), zone.id] as const),
  ),
);

// Default zone to fall back to per city / per state when no suburb match is
// found. Keeps inferZoneIdFromLocation() from mis-assigning every non-NSW
// listing to Sydney CBD now that Melbourne/Brisbane/Perth/Adelaide/Canberra
// have their own zones.
const CITY_DEFAULT_ZONE: Record<string, ZoneId> = {
  sydney: "sydney-cbd",
  melbourne: "melbourne-cbd",
  brisbane: "brisbane-cbd",
  perth: "perth",
  adelaide: "adelaide",
  canberra: "canberra",
};

const STATE_DEFAULT_ZONE: Partial<Record<Exclude<StateCode, "ALL">, ZoneId>> = {
  NSW: "sydney-cbd",
  VIC: "melbourne-cbd",
  QLD: "brisbane-cbd",
  WA: "perth",
  SA: "adelaide",
  ACT: "canberra",
};

export function isZoneId(value: string | null | undefined): value is ZoneId {
  return !!value && ZONE_BY_ID.has(value as ZoneId);
}

export function getZoneById(zoneId: string | null | undefined): Zone | undefined {
  return isZoneId(zoneId) ? ZONE_BY_ID.get(zoneId) : undefined;
}

export function getDefaultZone(): Zone {
  return ZONE_BY_ID.get(DEFAULT_ZONE_ID) ?? ZONES[0];
}

export function getDefaultZoneForSuburb(
  suburb: string | null | undefined,
  state?: string | null,
): Zone {
  const inferredZoneId = inferZoneIdFromLocation({ suburb, state });
  return getZoneById(inferredZoneId) ?? getDefaultZone();
}

export function inferZoneIdFromLocation(input: {
  zoneId?: string | null;
  suburb?: string | null;
  city?: string | null;
  state?: string | null;
}): ZoneId {
  if (isZoneId(input.zoneId)) {
    return input.zoneId;
  }

  const suburbKey = normalizeLocationToken(input.suburb);
  if (suburbKey) {
    const zoneId = SUBURB_TO_ZONE.get(suburbKey);
    if (zoneId) return zoneId;
  }

  const cityKey = normalizeLocationToken(input.city);
  const cityDefault = CITY_DEFAULT_ZONE[cityKey];
  if (cityDefault) {
    return cityDefault;
  }

  const state = input.state?.toUpperCase();
  if (state && isValidStateCode(state) && state !== "ALL") {
    const stateDefault = STATE_DEFAULT_ZONE[state];
    if (stateDefault) return stateDefault;
  }

  return DEFAULT_ZONE_ID;
}

export function getFallbackZones(zoneId: string | null | undefined): Zone[] {
  const zone = getZoneById(zoneId) ?? getDefaultZone();
  return zone.fallbackZoneIds
    .map((fallbackZoneId) => getZoneById(fallbackZoneId))
    .filter((fallbackZone): fallbackZone is Zone => !!fallbackZone);
}

export function normalizeLocationToken(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
}
